export type Stage = 'delivered' | 'inProgress' | 'upNext'

export type BetStatus =
  | 'Doubling down'
  | 'Evaluating'
  | 'Exploring'
  | 'Pivoting'
  | 'Killing'

export type BetCategory = string

export type PersonRole = 'Head of' | 'Employee'

export interface Category {
  id: string
  name: string
  color: string
}

export interface Person {
  id: string
  name: string
  role: PersonRole
}

export interface ProofPoint {
  id: string
  text: string
  date: string
  score: number
  ownerId: string | null
  ownerName: string | null
  createdAt?: string
}

export interface Bet {
  id: string
  thesis: string
  category: Category
  owner: Person
  status: BetStatus
  delivered: ProofPoint[]
  inProgress: ProofPoint[]
  upNext: ProofPoint[]
}

export interface DragItem {
  type: 'PROOF_POINT'
  proofId: string
  betId: string
  fromStage: Stage
}

export const DRAG_TYPE = 'PROOF_POINT' as const

export const STAGE_CONFIG: Record<
  Stage,
  { label: string; icon: string; accent: string; bg: string; border: string }
> = {
  delivered: {
    label: 'Delivered',
    icon: '✅',
    accent: '#10b981',
    bg: '#f0fdf4',
    border: '#bbf7d0',
  },
  inProgress: {
    label: 'In Progress',
    icon: '🔄',
    accent: '#f59e0b',
    bg: '#fffbeb',
    border: '#fde68a',
  },
  upNext: {
    label: 'Up Next',
    icon: '💡',
    accent: '#8b5cf6',
    bg: '#faf5ff',
    border: '#ddd6fe',
  },
}

export const STATUS_STYLES: Record<BetStatus, { bg: string; fg: string }> = {
  'Doubling down': { bg: '#dbeafe', fg: '#1e40af' },
  Evaluating: { bg: '#fef3c7', fg: '#92400e' },
  Exploring: { bg: '#f3e8ff', fg: '#6b21a8' },
  Pivoting: { bg: '#fee2e2', fg: '#991b1b' },
  Killing: { bg: '#fecaca', fg: '#991b1b' },
}

/** Default color palette for category pills — used as fallback */
const DEFAULT_CATEGORY_COLORS: Record<string, { bg: string; fg: string }> = {
  Growth: { bg: '#ede9fe', fg: '#5b21b6' },
  Product: { bg: '#e0f2fe', fg: '#0369a1' },
  Strategy: { bg: '#fce7f3', fg: '#be185d' },
}

/** Get category style — falls back to neutral if name is not in the palette */
export const getCategoryStyle = (name: string): { bg: string; fg: string } =>
  DEFAULT_CATEGORY_COLORS[name] ?? { bg: '#f3f4f6', fg: '#374151' }

export const scoreColor = (s: number): string =>
  s >= 4 ? '#10b981' : s >= 3 ? '#f59e0b' : '#ef4444'

export const avgScore = (items: ProofPoint[]): number => {
  if (!items.length) return 0
  return items.reduce((s, p) => s + p.score, 0) / items.length
}

const TODAY = new Date('2026-03-03')
export const daysAgo = (d: string, n: number): boolean => {
  const t = new Date(d)
  const cut = new Date(TODAY)
  cut.setDate(cut.getDate() - n)
  return t >= cut
}
